import { api_configuration } from "../../common";
import api_application_identifier = api_configuration.types.application_identifier;

export type Raw_Id_Path_Head = {
	// eslint-disable-next-line @typescript-eslint/ban-types
	"has steps": ["no", {}] | ["yes", RawIdPathStep];
};
type RawIdPathStep = {
	type:
		| ["group", { group: string }]
		| [
				"state",
				{
					"state group": string;
					state: string;
				}
		  ]
		| [
				"collection entry",
				{
					collection: string;
					id: string;
				}
		  ];
	tail: Raw_Id_Path_Head;
};

type BuildStep = (tail: Raw_Id_Path_Head) => Raw_Id_Path_Head;
export class IdPathBuilder {
	build_steps: BuildStep[];
	constructor(build_steps: BuildStep[] = []) {
		this.build_steps = build_steps;
	}

	static serialize(id_path_builder: IdPathBuilder): string {
		return (function step(path: Raw_Id_Path_Head): string {
			switch (path["has steps"][0]) {
				case "no":
					return "";
				case "yes": {
					const type = path["has steps"][1].type;
					const tail = path["has steps"][1].tail;
					switch (type[0]) {
						case "group":
							return "+'" + type[1]["group"] + "'" + step(tail);
						case "state":
							return "?'" + type[1]["state group"] + "'*'" + type[1]["state"] + "'" + step(tail);
						case "collection entry":
							return ".'" + type[1]["collection"] + "':'" + type[1]["id"] + "'" + step(tail);
						default:
							throw new Error("unexpected step");
					}
				}
				default:
					throw new Error("unexpected step");
			}
		})(id_path_builder.build());
	}

	static parse(
		path: string,
		onSuccess: (id_path_builder: IdPathBuilder) => void,
		onError: (message: string) => void
	): void {
		function assert(assertion: boolean) {
			if (assertion === false) {
				throw "";
			}
		}
		function parseLiteral(char_stream: string): string {
			assert(char_stream.charAt(0) === "'");
			let done = false;
			let i = 1;
			while (!done) {
				if (char_stream.charAt(i) === "\\") {
					i = i + 2;
				} else if (char_stream.charAt(i) === "'") {
					done = true;
				} else if (char_stream.charAt(i) === "") {
					throw "";
				} else {
					i = i + 1;
				}
			}
			return char_stream.substring(1, i);
		}

		let id_path_builder = IdPathBuilder.create();
		let error: string | undefined = undefined;
		try {
			let char_stream = path.slice();
			while (char_stream !== "") {
				switch (char_stream.charAt(0)) {
					case "+": {
						char_stream = char_stream.substring(1);
						const group = parseLiteral(char_stream);
						char_stream = char_stream.substring(group.length + 2);
						id_path_builder = id_path_builder.group(group);
						break;
					}
					case "?": {
						char_stream = char_stream.substring(1);
						const state_group = parseLiteral(char_stream);
						char_stream = char_stream.substring(state_group.length + 2);
						assert(char_stream.charAt(0) === "*");
						char_stream = char_stream.substring(1);
						const state = parseLiteral(char_stream);
						char_stream = char_stream.substring(state.length + 2);
						id_path_builder = id_path_builder.state(state_group, state);
						break;
					}
					case ".": {
						char_stream = char_stream.substring(1);
						const collection = parseLiteral(char_stream);
						char_stream = char_stream.substring(collection.length + 2);
						assert(char_stream.charAt(0) === ":");
						char_stream = char_stream.substring(1);
						const key = parseLiteral(char_stream);
						char_stream = char_stream.substring(key.length + 2);
						id_path_builder = id_path_builder.collectionEntry(collection, key);
						break;
					}
					default:
						throw "";
				}
			}
		} catch (e) {
			error = e;
		}
		if (error !== undefined) {
			onError(error);
		} else {
			onSuccess(id_path_builder);
		}
	}

	static create(): IdPathBuilder {
		return new IdPathBuilder();
	}

	equals(that: IdPathBuilder): boolean {
		return IdPathBuilder.serialize(this) === IdPathBuilder.serialize(that);
	}

	copyBuildSteps(): BuildStep[] {
		return this.build_steps.slice(0);
	}
	addToBuildStepArray(element: BuildStep): BuildStep[] {
		const copy = this.copyBuildSteps();
		copy.push(element);
		return copy;
	}
	newBuilder(build_function: BuildStep): IdPathBuilder {
		return new IdPathBuilder(this.addToBuildStepArray(build_function));
	}
	clone(): IdPathBuilder {
		return new IdPathBuilder(this.copyBuildSteps());
	}
	removeTail(): IdPathBuilder {
		return new IdPathBuilder(this.build_steps.slice(0, this.build_steps.length - 1));
	}

	group(group: string): IdPathBuilder {
		return this.newBuilder(function (tail: Raw_Id_Path_Head) {
			return {
				"has steps": [
					"yes",
					{
						type: ["group", { group: group }],
						tail: tail,
					},
				],
			};
		});
	}

	state(state_group: string, state: string): IdPathBuilder {
		return this.newBuilder(function (tail: Raw_Id_Path_Head) {
			return {
				"has steps": [
					"yes",
					{
						type: [
							"state",
							{
								"state group": state_group,
								state: state,
							},
						],
						tail: tail,
					},
				],
			};
		});
	}

	collectionEntry(collection: string, key: string): IdPathBuilder {
		return this.newBuilder(function (tail: Raw_Id_Path_Head) {
			return {
				"has steps": [
					"yes",
					{
						type: [
							"collection entry",
							{
								collection: collection,
								id: key,
							},
						],
						tail: tail,
					},
				],
			};
		});
	}

	build(): Raw_Id_Path_Head {
		return this.copyBuildSteps()
			.reverse()
			.reduce(
				function (tail, build_step) {
					return build_step(tail);
				},
				{ "has steps": ["no", {}] }
			);
	}
}

export function entityBuilderOf(builder: IdPathBuilder): IdPathBuilder {
	return entityIdPathBuilder(
		api_application_identifier.alan_api.Capplication_identifier.create({
			path: builder.build(),
		}).properties.path,
		{
			entity_builder: IdPathBuilder.create(),
			current_builder: IdPathBuilder.create(),
		}
	);
}

function entityIdPathBuilder(
	id_path: api_application_identifier.alan_api.Cid_path,
	builder: { entity_builder: IdPathBuilder; current_builder: IdPathBuilder }
): IdPathBuilder {
	return id_path.properties.has_steps.switch({
		no: builder.entity_builder,
		yes: function ($) {
			function singularStep(step: IdPathBuilder) {
				return {
					entity_builder: builder.entity_builder,
					current_builder: step,
				};
			}
			function entityStep(step: IdPathBuilder) {
				return {
					entity_builder: step,
					current_builder: step,
				};
			}
			return entityIdPathBuilder(
				$.properties.tail,
				$.properties.type.switch({
					"collection entry": function ($) {
						return entityStep(builder.current_builder.collectionEntry($.properties.collection, $.properties.id));
					},
					group: function ($) {
						return singularStep(builder.current_builder.group($.properties.group));
					},
					state: function ($) {
						return singularStep(builder.current_builder.state($.properties.state_group, $.properties.state));
					},
				})
			);
		},
	});
}

export function stepsAreEqual(step_a: RawIdPathStep, step_b: RawIdPathStep): boolean {
	if (step_a.type[0] !== step_b.type[0]) {
		return false;
	}
	switch (step_a.type[0]) {
		case "group":
			return step_a.type[0] === step_b.type[0] && step_a.type[1].group === step_b.type[1].group;
		case "state":
			return (
				step_a.type[0] === step_b.type[0] &&
				step_a.type[1]["state group"] === step_b.type[1]["state group"] &&
				step_a.type[1].state === step_b.type[1].state
			);

		case "collection entry":
			return (
				step_a.type[0] === step_b.type[0] &&
				step_a.type[1].collection === step_b.type[1].collection &&
				step_a.type[1].id === step_b.type[1].id
			);
	}
}
